// Creative Strategist chat composer (Task #2377).
//
// Attachments upload the moment they are picked (XHR so the progress bar is
// real, one request per file so a rejection names the file it belongs to).
// Each upload becomes a Context Asset immediately — it shows up in the Files
// tab whether or not the message is ever sent — and the send call only passes
// the asset ids.
//
// The composer stays usable while the strategist is thinking: nothing here is
// disabled by a pending reply.

import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Loader2, Paperclip, Send, X, Crosshair, FileText, AlertCircle } from "lucide-react";
import { thumbUrl, thumbFallback } from "@/lib/img-thumb";
import {
  CS_ACCEPTED_LABEL,
  CS_MAX_FILE_BYTES,
  CS_MAX_FILES_PER_REQUEST,
  CS_UPLOAD_ACCEPT,
  csAssetsKey,
  csFormatBytes,
  csIsImageAsset,
  type CreativeStrategistContextAsset,
} from "@/lib/creative-strategist";

export interface StagedUpload {
  key: string;
  name: string;
  size: number;
  previewUrl: string | null;
  progress: number;
  status: "uploading" | "done" | "error";
  asset?: CreativeStrategistContextAsset;
  error?: string;
}

const ACCEPTED_EXTENSIONS = new Set([
  "png", "jpg", "jpeg", "webp", "pdf", "docx", "txt", "md", "csv", "xlsx",
]);

function extensionOf(name: string): string {
  const base = String(name || "").split(/[\\/]/).pop() || "";
  const dot = base.lastIndexOf(".");
  return dot > 0 ? base.slice(dot + 1).toLowerCase() : "";
}

/** Same rules the server enforces — this only saves a doomed round trip. */
function localReason(file: File): string | null {
  if (file.size > CS_MAX_FILE_BYTES) {
    return `Too large (max ${csFormatBytes(CS_MAX_FILE_BYTES)})`;
  }
  if (file.size === 0) return "The file is empty";
  if (!ACCEPTED_EXTENSIONS.has(extensionOf(file.name))) {
    return `Unsupported file type (${CS_ACCEPTED_LABEL})`;
  }
  return null;
}

function xhrUpload(
  url: string,
  form: FormData,
  onProgress: (pct: number) => void,
): Promise<any> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", url);
    xhr.withCredentials = true;
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => {
      let data: any = {};
      try {
        data = JSON.parse(xhr.responseText);
      } catch {
        /* non-JSON body — fall through to the status check */
      }
      if (xhr.status >= 200 && xhr.status < 300) resolve(data);
      else reject(new Error(data?.message || `Upload failed (${xhr.status})`));
    };
    xhr.onerror = () => reject(new Error("Upload failed"));
    xhr.send(form);
  });
}

export function ChatComposer({
  projectId,
  targetLabel,
  targetIgnored,
  onToggleTarget,
  onSend,
  sending,
  onAssetsChanged,
}: {
  projectId: string;
  targetLabel: string | null;
  targetIgnored: boolean;
  onToggleTarget: () => void;
  onSend: (input: { body: string; attachmentIds: string[] }) => Promise<void>;
  sending: boolean;
  onAssetsChanged: () => void;
}) {
  const [text, setText] = useState("");
  const [staged, setStaged] = useState<StagedUpload[]>([]);
  const fileRef = useRef<HTMLInputElement>(null);
  const stagedRef = useRef(staged);
  stagedRef.current = staged;

  // Object URLs are per-file previews; release them when the composer dies.
  useEffect(() => {
    return () => {
      for (const s of stagedRef.current) {
        if (s.previewUrl) URL.revokeObjectURL(s.previewUrl);
      }
    };
  }, []);

  const patch = (key: string, next: Partial<StagedUpload>) =>
    setStaged((prev) => prev.map((s) => (s.key === key ? { ...s, ...next } : s)));

  const pickFiles = async (files: File[]) => {
    const room = CS_MAX_FILES_PER_REQUEST - staged.length;
    const batch = files.slice(0, Math.max(0, room));
    if (batch.length === 0) return;

    const rows: StagedUpload[] = batch.map((file, i) => ({
      key: `${Date.now()}-${i}-${file.name}`,
      name: file.name,
      size: file.size,
      previewUrl: file.type.startsWith("image/") ? URL.createObjectURL(file) : null,
      progress: 0,
      status: "uploading",
    }));
    setStaged((prev) => [...prev, ...rows]);

    // Sequential: one file in flight at a time keeps the progress readable and
    // the upstream request small.
    for (let i = 0; i < batch.length; i++) {
      const row = rows[i];
      const reason = localReason(batch[i]);
      if (reason) {
        patch(row.key, { status: "error", error: reason });
        continue;
      }
      try {
        const form = new FormData();
        form.append("files", batch[i]);
        const data = await xhrUpload(csAssetsKey(projectId), form, (pct) =>
          patch(row.key, { progress: pct }),
        );
        const asset: CreativeStrategistContextAsset | undefined = data?.assets?.[0];
        if (!asset) {
          patch(row.key, {
            status: "error",
            error: data?.rejected?.[0]?.message || "Upload failed",
          });
        } else {
          patch(row.key, { status: "done", progress: 100, asset });
          onAssetsChanged();
        }
      } catch (e: any) {
        patch(row.key, { status: "error", error: e?.message || "Upload failed" });
      }
    }
  };

  const removeStaged = (key: string) => {
    setStaged((prev) => {
      const row = prev.find((s) => s.key === key);
      if (row?.previewUrl) URL.revokeObjectURL(row.previewUrl);
      return prev.filter((s) => s.key !== key);
    });
  };

  const ready = staged.filter((s) => s.status === "done" && s.asset);
  const uploading = staged.some((s) => s.status === "uploading");
  const canSend = (text.trim().length > 0 || ready.length > 0) && !uploading && !sending;

  const submit = async () => {
    if (!canSend) return;
    const body = text.trim();
    const attachmentIds = ready.map((s) => s.asset!.id);
    await onSend({ body, attachmentIds });
    setText("");
    for (const s of staged) if (s.previewUrl) URL.revokeObjectURL(s.previewUrl);
    setStaged([]);
  };

  return (
    <div className="border-t bg-background p-3 space-y-2" data-testid="creative-strategist-composer">
      {targetLabel && (
        <button
          type="button"
          onClick={onToggleTarget}
          className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] transition-colors ${
            targetIgnored
              ? "text-muted-foreground hover:text-foreground"
              : "border-primary/40 bg-primary/5 text-foreground"
          }`}
          data-testid="button-chat-target"
          title={
            targetIgnored
              ? "This message is about the whole project — click to talk about what you're viewing"
              : "This message is about what you're viewing — click to talk about the whole project instead"
          }
        >
          <Crosshair className="h-3 w-3" />
          {targetIgnored ? "Talking about: this project" : `Talking about: ${targetLabel}`}
        </button>
      )}

      {staged.length > 0 && (
        <div className="space-y-1.5" data-testid="list-staged-uploads">
          {staged.map((s) => (
            <div
              key={s.key}
              className="flex items-center gap-2 rounded-md border p-1.5"
              data-testid={`staged-upload-${s.key}`}
            >
              {s.previewUrl ? (
                <img
                  src={s.previewUrl}
                  alt=""
                  className="h-8 w-8 rounded object-cover shrink-0"
                />
              ) : (
                <div className="h-8 w-8 rounded bg-muted flex items-center justify-center shrink-0">
                  <FileText className="h-4 w-4 text-muted-foreground" />
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <span className="text-xs truncate">{s.name}</span>
                  <span className="text-[10px] text-muted-foreground shrink-0">
                    {csFormatBytes(s.size)}
                  </span>
                </div>
                {s.status === "uploading" && <Progress value={s.progress} className="h-1 mt-1" />}
                {s.status === "error" && (
                  <div className="flex items-center gap-1 text-[10px] text-destructive mt-0.5">
                    <AlertCircle className="h-3 w-3 shrink-0" />
                    {s.error}
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={() => removeStaged(s.key)}
                className="text-muted-foreground hover:text-foreground shrink-0"
                data-testid={`button-remove-staged-${s.key}`}
                aria-label={`Remove ${s.name}`}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-end gap-2">
        <input
          ref={fileRef}
          type="file"
          multiple
          accept={CS_UPLOAD_ACCEPT}
          className="hidden"
          onChange={(e) => {
            const files = Array.from(e.target.files ?? []);
            e.target.value = "";
            void pickFiles(files);
          }}
          data-testid="input-chat-files"
        />
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="shrink-0"
          onClick={() => fileRef.current?.click()}
          disabled={staged.length >= CS_MAX_FILES_PER_REQUEST}
          title={`Attach files (${CS_ACCEPTED_LABEL})`}
          data-testid="button-attach-files"
        >
          <Paperclip className="h-4 w-4" />
        </Button>
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              void submit();
            }
          }}
          rows={2}
          placeholder="Tell the strategist what you want…"
          className="resize-none text-sm min-h-[44px]"
          data-testid="input-chat-message"
        />
        <Button
          type="button"
          size="icon"
          className="shrink-0"
          onClick={() => void submit()}
          disabled={!canSend}
          data-testid="button-send-message"
          aria-label="Send message"
        >
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </Button>
      </div>
    </div>
  );
}

/** Attachment strip under a sent message. */
export function MessageAttachments({ assets }: { assets: CreativeStrategistContextAsset[] }) {
  if (assets.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid="message-attachments">
      {assets.map((asset) =>
        csIsImageAsset(asset) ? (
          <a
            key={asset.id}
            href={asset.storageUrl}
            target="_blank"
            rel="noreferrer"
            title={asset.fileName}
            data-testid={`attachment-${asset.id}`}
          >
            <img
              src={thumbUrl(asset.storageUrl, 160) ?? asset.storageUrl}
              onError={thumbFallback(asset.storageUrl)}
              alt={asset.fileName}
              className="h-16 w-16 rounded-md object-cover border"
            />
          </a>
        ) : (
          <a
            key={asset.id}
            href={asset.storageUrl}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-1.5 rounded-md border px-2 py-1 text-[11px] hover:bg-muted transition-colors max-w-[180px]"
            title={asset.fileName}
            data-testid={`attachment-${asset.id}`}
          >
            <FileText className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            <span className="truncate">{asset.fileName}</span>
          </a>
        ),
      )}
    </div>
  );
}
